import React, { Component, createRef } from "react";
import PropTypes from "prop-types";
import { OFFICER_VETTED_STATUS, SUPPERVISOR_APPROVAL_MENU } from "@constants";
import { ShiftService } from "@services";
import utils from "@utils";
import { AddEditShiftModal } from "./components";
import { Image, ScrollDetect, ConfirmationModal, TabMenu } from "../../components";

import "./ShiftList.scss";

class ShiftList extends Component {
	constructor(props) {
		super(props);
		const {
			router: {
				location: { query },
			},
		} = props;
		this.state = {
			shifts: [],
			expandedShifts: [],
			shiftOfficers: {},
			pageNumber: Number.isNaN(parseInt(query.pageNumber, 10))
				? 1
				: parseInt(query.pageNumber, 10),
			hasMore: true,
			filters: {
				officerName: query.officerName || "",
				supervisorName: query.supervisorName || "",
				shiftName: query.shiftName || "",
			},
			showAddEditModal: false,
			editShiftId: undefined,
		};
		this._confirmationModal = createRef();
	}

	/** Retrieves a paginated list of shifts */
	getShifts = () => {
		const { lang } = global;
		const { pageNumber, filters } = this.state;
		const {
			router: { push },
		} = this.props;
		const displayCount = 25;
		let query = {
			pageNumber,
			displayCount,
			...filters,
		};
		// Clean up empty query params
		query = Object.fromEntries(Object.entries(query).filter(([, value]) => value !== ""));

		ShiftService.getAll(query)
			.then(({ data: { ItemList } }) => {
				this.setState(prevState => ({
					shifts:
						prevState.pageNumber === 1 ? ItemList : [...prevState.shifts, ...ItemList],
					pageNumber: prevState.pageNumber + 1,
					hasMore: ItemList.length >= displayCount,
				}));
				push(`/admin/shift/list?${new URLSearchParams(query).toString()}`);
			})
			.catch(() => {
				this.setState({ pageNumber: 1, hasMore: false });
				utils.notify({
					type: "error",
					message: lang.t("Error getting shifts"),
					timeOut: 2000,
				});
			});
	};

	/**
	 * Retrieves a list of officers assigned to a specific shift
	 * @param {Number} shiftId The unique id of a shift
	 */
	getOfficersForShift = shiftId => {
		const { lang } = global;
		ShiftService.getById(shiftId)
			.then(({ data: { Officers } }) =>
				this.setState(prevState => ({
					shiftOfficers: { ...prevState.shiftOfficers, [shiftId]: Officers },
				})),
			)
			.catch(() =>
				utils.notify({
					type: "error",
					message: lang.t("Error getting Officers"),
					timeOut: 2000,
				}),
			);
	};

	toggleShiftOfficers = shiftId => {
		this.setState(prevState => {
			const { expandedShifts, shiftOfficers } = prevState;
			if (!shiftOfficers[shiftId] && !expandedShifts.includes(shiftId))
				this.getOfficersForShift(shiftId);
			if (!expandedShifts.includes(shiftId)) {
				expandedShifts.push(shiftId);
				return { expandedShifts };
			}
			return { expandedShifts: expandedShifts.filter(id => id !== shiftId) };
		});
	};

	/**
	 * Handles any changes made to the filters of the shift list which will in turn filter the list items
	 * @param {String} property The filet being changed
	 */
	handleFilterChange = property => event => {
		let value = "";
		if (property === "officerName" || property === "supervisorName" || property === "shiftName")
			({
				target: { value },
			} = event);
		else if (event) ({ value } = event);

		this.setState(
			prevState => ({
				pageNumber: property !== "officerName" ? 1 : prevState.pageNumber,
				filters: { ...prevState.filters, [property]: value },
			}),
			() => {
				if (
					property !== "officerName" &&
					property !== "supervisorName" &&
					property !== "shiftName"
				)
					this.getShifts();
			},
		);
	};

	applyNameFilter = () => this.setState({ pageNumber: 1 }, () => this.getShifts());

	/**
	 * Displays the status associated with a shift
	 * @param {Number} statusId THe uniqe id used to indentify a shifts status
	 * @returns {'null'| HTMLElement} The status label which will be displayed
	 */
	statusLabel = statusId => {
		const status = OFFICER_VETTED_STATUS.find(item => item.value === statusId);
		if (status) return <span className={`label label-${status.color}`}>{status.label}</span>;
		return null;
	};

	/**
	 * Displays a modal which prompts the user to confirm the deletion of a shift
	 * @param {Number} shiftId The uniqe id of the selected shift
	 */
	openDeleteConfirmationModal = shiftId => {
		const { lang } = global;
		const { shifts } = this.state;
		const shift = shifts.find(({ Id }) => shiftId === Id);
		this._confirmationModal.openModal({
			title: lang.t("Delete Shift"),
			message: lang.t("dynamic.Are you sure you want to delete", {
				subject: shift?.Name || "this shift",
			}),
			onConfirm: () => {
				ShiftService.delete(shiftId)
					.then(() => {
						this.setState(prevState => ({
							shifts: prevState.shifts.filter(item => item.Id !== shiftId),
							expandedShifts: prevState.expandedShifts.filter(id => id !== shiftId),
							shiftOfficers: Object.fromEntries(
								Object.entries(prevState.shiftOfficers).filter(
									([key]) => key !== shiftId,
								),
							),
						}));
						utils.notify({
							type: "success",
							message: lang.t("Shift deleted successfully"),
						});
					})
					.catch(() => {
						utils.notify({
							type: "error",
							message: lang.t("Error deleting shift"),
							timeOut: 1500,
						});
					});
			},
		});
	};

	toggleAddEditModal = id =>
		this.setState(prevState => ({
			showAddEditModal: !prevState.showAddEditModal,
			editShiftId: id,
		}));

	onClickTab = ({ tabNumber, url }) => {
		const {
			router: { push },
		} = this.props;
		const thisTab = SUPPERVISOR_APPROVAL_MENU.find(tab => tab.type === "management");
		if (tabNumber !== thisTab.tabNumber) push(url);
	};

	/** Resets the page state upon successfully adding a new shift in order to display the most up to date list */
	onAddEditShiftSuccess = () => {
		const { editShiftId: editedShift } = this.state;
		this.setState(
			{
				showAddEditModal: false,
				editShiftId: undefined,
				pageNumber: 1,
			},
			() => {
				this.getShifts();
				if (editedShift) this.getOfficersForShift(editedShift);
			},
		);
	};

	render() {
		const { lang } = global;
		const {
			shifts,
			expandedShifts,
			shiftOfficers,
			hasMore,
			filters,
			showAddEditModal,
			editShiftId,
		} = this.state;
		const approvalTabs = SUPPERVISOR_APPROVAL_MENU;

		return (
			<div
				className="container"
				id="shift-list"
			>
				<TabMenu
					tabs={approvalTabs}
					activeTab={approvalTabs.find(tab => tab.type === "management").tabNumber}
					onClickTab={this.onClickTab}
				/>
				<div className="row title-row">
					<div className="col-xs-8 col-sm-10">
						<h2>{lang.t("Supervisor Management")}</h2>
					</div>
					<div className="col-xs-4 col-sm-2">
						<button
							type="button"
							className="btn btn-primary btn-block"
							id="new-shift"
							onClick={() => this.toggleAddEditModal()}
						>
							+ {lang.t("New Shift")}
						</button>
					</div>
				</div>
				<div className="shift-filters">
					<div className="row">
						<div className="col-sm-4 form-group">
							<label htmlFor="officer-name">{lang.t("Personnel Name")}</label>
							<div className="input-group">
								<input
									id="officer-name"
									name="officer-name"
									className="form-control"
									placeholder={lang.t("Search by officer")}
									value={filters.officerName}
									onChange={this.handleFilterChange("officerName")}
									onKeyPress={({ key }) => {
										if (key === "Enter") this.applyNameFilter();
									}}
								/>
								<span className="input-group-btn">
									<button
										type="button"
										className="btn btn-default"
										onClick={this.applyNameFilter}
									>
										<i className="rkf rkf-search" />
									</button>
								</span>
							</div>
						</div>
						<div className="col-sm-4 form-group">
							<label htmlFor="supervisor-name">{lang.t("Supervisor Name")}</label>
							<div className="input-group">
								<input
									id="supervisor-name"
									name="supervisor-name"
									className="form-control"
									placeholder={lang.t("Search by Supervisor")}
									value={filters.supervisorName}
									onChange={this.handleFilterChange("supervisorName")}
									onKeyPress={({ key }) => {
										if (key === "Enter") this.applyNameFilter();
									}}
								/>
								<span className="input-group-btn">
									<button
										type="button"
										className="btn btn-default"
										onClick={this.applyNameFilter}
									>
										<i className="rkf rkf-search" />
									</button>
								</span>
							</div>
						</div>
						<div className="col-sm-4 form-group">
							<label htmlFor="shift-name">{lang.t("Shift Name")}</label>
							<div className="input-group">
								<input
									id="shift-name"
									name="shift-name"
									className="form-control"
									placeholder={lang.t("Search by Shift Name")}
									value={filters.shiftName}
									onChange={this.handleFilterChange("shiftName")}
									onKeyPress={({ key }) => {
										if (key === "Enter") this.applyNameFilter();
									}}
								/>
								<span className="input-group-btn">
									<button
										type="button"
										className="btn btn-default"
										onClick={this.applyNameFilter}
									>
										<i className="rkf rkf-search" />
									</button>
								</span>
							</div>
						</div>
					</div>
				</div>
				<div className="list-body">
					<For
						each="shift"
						of={shifts}
					>
						<div
							className="row"
							key={`officer-shift-${shift.Id}`}
						>
							<div className="col-xs-2 col-sm-1 text-center">
								<button
									type="button"
									className={`btn btn-default ${shift.TotalOfficers === 0 ? "hidden" : ""}`}
									onClick={() => this.toggleShiftOfficers(shift.Id)}
									title={
										expandedShifts.includes(shift.Id)
											? lang.t("Hide officers")
											: lang.t("Show officers")
									}
								>
									<i
										className={`rkf rkf-chevron-${expandedShifts.includes(shift.Id) ? "up" : "down"}`}
									/>
								</button>
							</div>
							<div className="col-xs-10 col-sm-11 supervisor">
								<div className="col-sm-2">
									<strong>{shift.Name}</strong>
								</div>
								<div className="col-sm-4 media">
									<div className="media-left media-middle">
										<Image
											src={shift.SupervisorProfilePhotoURL}
											className="img-circle media-object"
											fallbackSrc="avatar"
										/>
									</div>
									<div className="media-body media-middle">
										{shift.SupervisorName}
										<br />
										<em>{lang.t("Supervisor")}</em>
									</div>
								</div>
								<div className="col-sm-2">
									{shift.TotalOfficers}
									&nbsp;
									{lang.t(shift.TotalOfficers !== 1 ? "Officers" : "Officer")}
								</div>
								<div className="col-sm-4">
									<button
										type="button"
										className="btn btn-primary"
										onClick={() => this.toggleAddEditModal(shift.Id)}
									>
										{lang.t("Edit")}
									</button>
									<button
										type="button"
										className="btn btn-primary"
										onClick={() => this.openDeleteConfirmationModal(shift.Id)}
									>
										{lang.t("Delete")}
									</button>
								</div>
							</div>
							<If condition={expandedShifts.includes(shift.Id)}>
								<For
									each="officer"
									of={shiftOfficers[shift.Id] || []}
								>
									<div
										className="col-xs-10 col-sm-11 col-xs-offset-2 col-sm-offset-1 shift-officer"
										key={`shift-officer-${officer.Id}`}
									>
										<div className="col-xs-6 col-sm-4 col-sm-offset-2 media">
											<div className="media-left media-middle">
												<Image
													src={officer.ContactPhotoUrl}
													className="img-circle media-object"
													fallbackSrc="avatar"
												/>
											</div>
											<div className="media-body media-middle">
												{officer.ContactName}
												<br />
												<small>{officer.OfficerRank}</small>
											</div>
										</div>
										<div className="col-xs-6 col-sm-3">
											{this.statusLabel(officer.VettedStatus)}
										</div>
									</div>
								</For>
							</If>
						</div>
					</For>
					<If condition={hasMore}>
						<ScrollDetect
							className="text-center"
							onVisible={this.getShifts}
						>
							<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
						</ScrollDetect>
					</If>
				</div>
				<ConfirmationModal
					ref={ref => {
						this._confirmationModal = ref;
					}}
				/>
				<AddEditShiftModal
					show={showAddEditModal}
					onHide={() => this.toggleAddEditModal()}
					editId={editShiftId}
					onSuccess={this.onAddEditShiftSuccess}
				/>
			</div>
		);
	}
}

ShiftList.propTypes = {
	router: PropTypes.object,
};

ShiftList.defaultProps = {
	router: {},
};

export default ShiftList;
